import { Icon, IDropdownOption, Stack } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  Dropdown,
  IconName,
  IKeyValuePairEditorClassnames,
  IKeyValuePairEditorStyles,
  useClassNames,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { RoleBinding } from '../../../../authz/gen/ai/h2o/authorization/v1/role_binding_pb';
import { Group } from '../../../../authz/gen/ai/h2o/user/v1/group_pb';
import { User } from '../../../../authz/gen/ai/h2o/user/v1/user_pb';
import { useRoles } from '../../../../authz/providers/RoleProvider';
import { useUsers } from '../../../../authz/providers/UsersProvider';
import { LabelIconTooltip, OutOfVersionSeverity } from '../../../../components/LabelIconTooltip/LabelIconTooltip';
import { groupRoles } from '../../utils';
import { MembersDropdown } from './MembersDropdown';
import { membersListStyles } from './styles';
import { MemberListItemProps } from './types';

export const MemberListItem = (props: MemberListItemProps) => {
  const {
    prepopulatedRoleBindings = [],
    usersInWorkspaceRolesList = [],
    updateRoleBindings,
    disableRemoveButton,
    readOnly = false,
  } = props;

  const { allRoles } = useRoles();
  const { usersAndGroups: availableUsersAndGroups = [], usersAndGroupsNameMap } = useUsers();

  // If there are prepopulated role bindings, the user is selected in EDIT mode as the assigned one.
  const prepopulatedUserOrGroup = useMemo(() => {
    if (prepopulatedRoleBindings.length > 0 && usersAndGroupsNameMap) {
      return usersAndGroupsNameMap[prepopulatedRoleBindings[0].subject || ''];
    }
    return undefined;
  }, [prepopulatedRoleBindings, usersAndGroupsNameMap]);
  const isGroup = prepopulatedUserOrGroup && prepopulatedUserOrGroup.name?.includes('group');
  const missingUserOrGroup = prepopulatedRoleBindings.length > 0 && !prepopulatedUserOrGroup;
  const [selectedUserOrGroup, setSelectedUserOrGroup] = useState<User | undefined>(prepopulatedUserOrGroup);
  const [selectedRoles, setSelectedRoles] = useState<RoleBinding[]>(prepopulatedRoleBindings);
  const [selectedRoleKeys, setSelectedRoleKeys] = useState<string[]>(() =>
    prepopulatedRoleBindings.map((role) => role.role || '')
  );
  const userRoleOptions = useMemo(() => {
    if (!allRoles) return [];

    // Check if both owner roles are selected
    const hasWorkspaceOwner = selectedRoleKeys?.includes('roles/workspace-owner') || false;
    const hasPersonalWorkspaceOwner = selectedRoleKeys?.includes('roles/personal-workspace-owner') || false;
    const bothOwnerRolesSelected = hasWorkspaceOwner && hasPersonalWorkspaceOwner;

    const roleOptions: IDropdownOption[] = allRoles.map((role) => {
      let disabled = false;

      // Handle owner role logic only when disableRemoveButton is true
      if (
        disableRemoveButton &&
        (role.name === 'roles/workspace-owner' || role.name === 'roles/personal-workspace-owner')
      ) {
        if (bothOwnerRolesSelected) {
          // Rule 1: If both options are selected - both options are enabled
          disabled = false;
        } else if (role.name === 'roles/workspace-owner' && hasWorkspaceOwner) {
          // Rule 2: If workspace-owner is selected, it's disabled from unselecting
          // BUT only if we don't have both owner roles
          disabled = !hasPersonalWorkspaceOwner;
        } else if (role.name === 'roles/personal-workspace-owner' && hasPersonalWorkspaceOwner) {
          // Rule 2: If personal-workspace-owner is selected, it's disabled from unselecting
          disabled = true;
        }
      }

      return {
        key: role.name || '',
        text: role.displayName || '',
        disabled,
      };
    });

    return groupRoles(roleOptions);
  }, [allRoles, disableRemoveButton, selectedRoleKeys]);
  const selectedUsersAndGroupsInList = useMemo(() => {
    return availableUsersAndGroups
      .filter((user) => {
        const userName = user.name?.trim().toLowerCase();
        return userName && usersInWorkspaceRolesList.some((role) => role.trim().toLowerCase() === userName);
      })
      .map(({ name }) => name!)
      .filter(Boolean);
  }, [availableUsersAndGroups, usersInWorkspaceRolesList]);

  const handleRoleUpdate = useCallback(
    (_e, roleOption) => {
      if (roleOption?.selected) {
        const newSelectedRoles = [
          ...selectedRoles,
          { role: String(roleOption.key), subject: selectedUserOrGroup?.name },
        ];
        setSelectedRoles(newSelectedRoles);
      } else {
        const newSelectedRoles = [...selectedRoles].filter((role) => role.role !== roleOption?.key);
        setSelectedRoles(newSelectedRoles);
      }

      if (prepopulatedUserOrGroup) {
        updateRoleBindings(
          [
            {
              role: String(roleOption.key),
              subject: selectedUserOrGroup?.name,
              name: prepopulatedRoleBindings.filter((binding) => binding.role === roleOption.key)[0]?.name,
            },
          ],
          roleOption?.selected
        );
      }
    },
    [selectedRoles, setSelectedRoles, selectedUserOrGroup, selectedRoleKeys]
  );
  const resetForm = useCallback(() => {
    setSelectedUserOrGroup({ name: '' });
    setSelectedRoles([]);
    setSelectedRoleKeys([]);
  }, [setSelectedUserOrGroup, setSelectedRoles, setSelectedRoleKeys]);
  const handleAddUser = useCallback(() => {
    updateRoleBindings(selectedRoles, true);
    resetForm();
  }, [selectedRoles, updateRoleBindings]);
  const handleRemoveAllUserBindings = useCallback(() => {
    updateRoleBindings(selectedRoles);
  }, [selectedRoles, updateRoleBindings]);

  useEffect(() => {
    if (selectedRoles) {
      const newSelectedRoleKeys = selectedRoles.map((role) => role.role || '');
      setSelectedRoleKeys(newSelectedRoleKeys);
    } else {
      setSelectedRoleKeys([]);
    }
  }, [selectedRoles, setSelectedRoleKeys]);

  // Update selectedRoleKeys when prepopulatedRoleBindings changes (for edit mode)
  useEffect(() => {
    if (prepopulatedRoleBindings.length > 0) {
      const newSelectedRoleKeys = prepopulatedRoleBindings.map((role) => role.role || '');
      setSelectedRoleKeys(newSelectedRoleKeys);
    }
  }, [prepopulatedRoleBindings]);

  const classNames = useClassNames<IKeyValuePairEditorStyles, IKeyValuePairEditorClassnames>(
    'MembersList',
    membersListStyles
  );

  return (
    <Stack horizontal horizontalAlign={'space-between'} className={classNames.body}>
      <div className={classNames.keyColumn}>
        {missingUserOrGroup ? (
          <Stack horizontal verticalAlign="center">
            <Stack styles={{ root: { margin: '0 4px 0 -24px' } }}>
              <LabelIconTooltip
                iconName={IconName.WarningSolid}
                tooltip={`
                  The user or group assigned to this role no longer exists.
                  You can remove this role binding.\n
                  (${prepopulatedRoleBindings[0].subject})
                `}
                severity={OutOfVersionSeverity.Warning}
              />
            </Stack>
            <span>Not found</span>
          </Stack>
        ) : (
          <>
            {prepopulatedUserOrGroup ? (
              <Stack horizontal verticalAlign="center">
                {isGroup && <Icon iconName={IconName.Group} style={{ marginRight: 10 }} title="Group" />}
                <span>{prepopulatedUserOrGroup?.displayName}</span>
              </Stack>
            ) : (
              <MembersDropdown
                onUserChange={(option) => setSelectedUserOrGroup(option as User | Group)}
                prepopulatedUser={selectedUserOrGroup}
                disableOptions={selectedUsersAndGroupsInList}
                fetchGroups
                disabled={readOnly}
              />
            )}
          </>
        )}
      </div>
      <div className={classNames.valueColumn}>
        <Dropdown
          multiSelect
          selectedKeys={selectedRoleKeys}
          onChange={handleRoleUpdate}
          placeholder="Select Permission"
          options={userRoleOptions}
          disabled={readOnly || !selectedUserOrGroup?.name}
        />
      </div>
      {!readOnly && (
        <div className={classNames.buttonColumn}>
          {prepopulatedUserOrGroup || missingUserOrGroup ? (
            <Button
              tooltip={disableRemoveButton ? 'Cannot remove the only Workspace owner' : ''}
              disabled={disableRemoveButton}
              styles={[buttonStylesGhost]}
              text="Remove"
              onClick={disableRemoveButton ? () => null : handleRemoveAllUserBindings}
            />
          ) : (
            <Button
              styles={[buttonStylesPrimary]}
              split
              text="Add User"
              onClick={handleAddUser}
              disabled={!selectedUserOrGroup || !selectedRoles?.length}
            />
          )}
        </div>
      )}
    </Stack>
  );
};
